import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import "@/index.css";
import { I18nProvider, useI18n } from "@/i18n/I18nProvider";
import Nav from "@/components/Nav";
import PresenterControls from "@/components/PresenterControls";
import Hero from "@/components/sections/Hero";
import Problem from "@/components/sections/Problem";
import Solution from "@/components/sections/Solution";
import Dimensions from "@/components/sections/Dimensions";
import Metrics from "@/components/sections/Metrics";
import Dashboard from "@/components/sections/Dashboard";
import Pricing from "@/components/sections/Pricing";
import Competitive from "@/components/sections/Competitive";
import Traction from "@/components/sections/Traction";
import Team from "@/components/sections/Team";
import CTA from "@/components/sections/CTA";
import Footer from "@/components/sections/Footer";
import { exportDeckToPdf } from "@/utils/exportPdf";
import { useSlideAnalytics } from "@/hooks/useSlideAnalytics";
import AnalyticsPage from "@/pages/AnalyticsPage";

function Deck() {
    const { lang, t } = useI18n();
    const [presentMode, setPresentMode] = useState(false);
    const [exporting, setExporting] = useState(null);

    // Lightweight analytics — tracks dwell per slide
    useSlideAnalytics(lang, { enabled: true });

    useEffect(() => {
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("in");
                        io.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.12 }
        );
        document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [lang]); // re-observe after language change

    const handleExport = async () => {
        if (exporting) return;
        setExporting({ step: 0, total: 11, label: "Preparing…" });
        try {
            await exportDeckToPdf({ onProgress: (p) => setExporting(p) });
        } catch (err) {
            console.error("PDF export failed", err);
        } finally {
            setExporting(null);
        }
    };

    return (
        <div className="App font-body relative overflow-x-hidden">
            <Nav onPresent={() => setPresentMode(true)} onExport={handleExport} />
            <PresenterControls
                active={presentMode}
                onExit={() => setPresentMode(false)}
            />
            <main>
                <Hero />
                <Problem />
                <Solution />
                <Dimensions />
                <Metrics />
                <Dashboard />
                <Pricing />
                <Competitive />
                <Traction />
                <Team />
                <CTA />
            </main>
            <Footer />

            {exporting && (
                <div
                    data-testid="export-overlay"
                    className="fixed inset-0 z-[100] grid place-items-center bg-[#04140D]/85 backdrop-blur-md"
                >
                    <div className="rounded-2xl border border-white/10 bg-[#071B12] p-8 max-w-md w-[90%] text-center">
                        <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00] mb-2">
                            {t.export.title}
                        </div>
                        <div className="font-display font-bold text-2xl tracking-tight">
                            {t.export.renderingSlide} {exporting.step} {t.export.of}{" "}
                            {exporting.total}
                        </div>
                        <div className="mt-2 text-xs text-white/55 uppercase tracking-widest">
                            {exporting.label}
                        </div>
                        <div className="mt-6 h-2 rounded-full bg-[#04140D] overflow-hidden">
                            <div
                                className="h-full bg-[#D4FF00] transition-all duration-300"
                                style={{
                                    width: `${Math.round((exporting.step / exporting.total) * 100)}%`,
                                }}
                            />
                        </div>
                        <div className="mt-4 text-[11px] text-white/45">
                            {t.export.keepOpen}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function App() {
    return (
        <I18nProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Deck />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                </Routes>
            </BrowserRouter>
        </I18nProvider>
    );
}

export default App;
